import styled, { css } from 'styled-components';

import { anchorCore, anchorVariants } from '../Anchor/styles';
import { filePickerTokens } from '../../tokens/components/FilePicker/tokens';

/* @internal */
export const FilePickerActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

/* @internal */
export const FilePickerHandler = styled.div``;

/* @internal */
export const FilePickerHandlerText = styled.span``;

/* @internal */
export const FilePickerHandlerLabel = styled.label`
  ${anchorCore}
  ${anchorVariants.link}
  cursor: pointer;
`;

// top calculation: 42px (input height) - 22px (icon size)
const FilePickerIcons = styled.div`
  display: none;
  margin-left: 12px;
`;

/* @internal */
export const FilePickerLoadingIcon = styled(FilePickerIcons)``;

/* @internal */
export const FilePickerUploadIcon = styled(FilePickerIcons)``;

/* @internal */
export const StyledFilePicker = styled.div`
  width: 300px;
  min-height: 42px;
  font-size: ${filePickerTokens.FontSizeBaseDefault};

  ${({ theme }) =>
    theme.fpSize === 'full' &&
    css`
      width: 100%;
    `};

  ${({ theme }) =>
    theme.fpCell &&
    css`
      background: none;
      border-radius: 0;
      border: none;
      width: ${theme.cellWidthValue ? theme.cellWidthValue : '100%'};
    `};
`;

/* @internal */
export const FilePickerDraggable = styled.label`
  background: ${filePickerTokens.BackgroundColorBaseDefault};
  border-color: ${filePickerTokens.BorderColorBaseDefault};
  border-radius: ${filePickerTokens.BorderRadiusBaseDefault};
  border-style: ${filePickerTokens.BorderStyleBaseDefault};
  border-width: ${filePickerTokens.BorderWidthBaseDefault};
  display: flex;
  flex-flow: column;
  height: 100%;
  padding: 8px 12px;
  position: relative;
  transition: border 80ms ease-out, box-shadow 80ms ease-out;

  ${({ theme }) =>
    theme.fpFilled &&
    css`
      border-style: ${filePickerTokens.BorderStyleBaseFilled};
    `};

  &:hover {
    border-color: ${filePickerTokens.BorderColorBaseHover};
    border-style: ${filePickerTokens.BorderStyleBaseHover};
  }

  &:has(:focus-visible) {
    border-color: ${filePickerTokens.BorderColorBaseKeyboardFocus};
    border-style: ${filePickerTokens.BorderStyleBaseKeyboardFocus};
    box-shadow: 0 0 0 ${filePickerTokens.ShadowWidthBaseKeyboardFocus} ${filePickerTokens.ShadowColorBaseKeyboardFocus};

    // LEGACY RETROFIT
    .ink-table--cell & {
      outline: 1px ${filePickerTokens.OutlineColorBaseKeyboardFocus};
      outline-offset: -1px;
    }
  }

  ${({ theme }) =>
    theme.fpInvalid &&
    !theme.fpCell &&
    css`
      border-color: ${filePickerTokens.BorderColorInvalidDefault};
      border-style: ${filePickerTokens.BorderStyleInvalidDefault};
      border-width: ${filePickerTokens.BorderWidthInvalidDefault};

      &:hover {
        border-color: ${filePickerTokens.BorderColorInvalidDefault};
      }

      &:has(:focus-visible) {
        border-color: ${filePickerTokens.BorderColorInvalidDefault};
        border-style: ${filePickerTokens.BorderStyleInvalidDefault};
        box-shadow: 0 0 0 ${filePickerTokens.ShadowWidthInvalidKeyboardFocus}
          ${filePickerTokens.ShadowColorInvalidKeyboardFocus};
      }

      // LEGACY RETROFIT
      .ink-table--cell & {
        border-color: #d91e18;
        border-width: 2px;

        &:hover {
          padding: 8px 12px;
        }

        &:has(:focus-visible) {
          outline: none;
        }

        ${theme.fpDisabled &&
        css`
          border: none;
        `};
      }
    `};

  ${({ theme }) =>
    theme.fpDisabled &&
    css`
      background: ${filePickerTokens.BackgroundColorBaseDisabled};
      border-color: ${filePickerTokens.BorderColorBaseDisabled};
      border-style: ${filePickerTokens.BorderStyleBaseDisabled};
      color: ${filePickerTokens.FontColorBaseDisabled};
      cursor: not-allowed;
      pointer-events: none;

      &:focus-visible {
        outline: none;
      }

      * {
        color: ${filePickerTokens.FontColorBaseDisabled};
        cursor: not-allowed;
        pointer-events: none;

        &:focus-visible {
          outline: none;
        }
      }
    `};

  ${({ theme }) =>
    theme.fpDragging &&
    css`
      ${FilePickerUploadIcon} {
        display: inline-flex;
      }
    `};

  ${({ theme }) =>
    theme.fpLoading &&
    css`
      ${FilePickerLoadingIcon} {
        display: inline-flex;
      }

      ${theme.fpDragging &&
      css`
        ${FilePickerLoadingIcon} {
          display: none;
        }
      `};
    `};

  ${({ theme }) =>
    theme.fpCell &&
    css`
      background: none;
      border-radius: 0;
      border: none;

      &:hover {
        border: none;
      }

      &:has(:focus-visible) {
        border: none;
        box-shadow: none;
        outline: none;
      }

      // LEGACY RETROFIT - BORDER
      .ink-table--cell & {
        position: initial;

        &::before {
          content: '';
          display: none;
          border: 1px solid #686865;
          position: absolute;
          bottom: -1px;
          left: -1px;
          right: -1px;
          top: -1px;
          pointer-events: none;
        }

        &:has(:focus-visible)::before {
          border-color: #176bb8;
          display: block;
        }

        ${theme.fpInvalid &&
        !theme.fpDisabled &&
        css`
          &::before,
          &:has(:focus-visible)::before {
            border: 2px solid #d91e18;
            display: block;
          }
        `};

        ${theme.fpDisabled &&
        css`
          background: ${filePickerTokens.BackgroundColorBaseDisabled};
          border: 0;
          outline: 0;
        `}
      }

      ${!theme.fpDisabled &&
      css`
        .ink-table--cell:hover &::before {
          display: block;
        }
      `}
    `};
`;

/* @internal */
export const FilePickerInput = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 0;
  line-height: 0;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

/* @internal */
export const FilePickerList = styled.ul`
  display: block;
  border-top-width: ${filePickerTokens.BorderWidthListDefault};
  border-top-style: ${filePickerTokens.BorderStyleListDefault};
  border-top-color: ${filePickerTokens.BorderColorListDefault};
  margin-top: 8px;
  padding-top: 8px;
`;

/* @internal */
export const FilePickerItem = styled.li`
  display: flex;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* @internal */
export const FilePickerItemControls = styled.span`
  margin-right: 12px;
  white-space: nowrap;
`;

/* @internal */
export const FilePickerItemSeparator = styled.span`
  margin: 0 8px;
  color: ${filePickerTokens.BorderColorBaseDefault};
`;

/* @internal */
export const FilePickerFileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* @internal */
export const FilePickerTableWrapper = styled.div`
  position: relative;
  z-index: 1;
`;
